// src/hooks/comp/useCorpreg.js
import { useEffect, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AuthContext } from "@context";
import { ROLES } from "@constants/roles";
import { get, post, put } from "@utils/axios_api";
import { toast } from 'react-toastify';

/* ==== 1) Yup Validation ==== */
const schema = yup.object().shape({
    region: yup
        .string()
        .required("관할구역을 선택하세요."),
    compName: yup
        .string()
        .required("법인명(사업장명)을 입력하세요.")
        .max(20, "최대 20자까지 가능합니다."),
    bizNo: yup
        .string()
        .required("사업자등록번호를 입력하세요.")
        .max(10, "사업자등록번호는 10자리까지 입력 가능합니다.")
        .matches(/^[0-9]+$/, "숫자만 입력 가능합니다."),
    repName: yup
        .string()
        .required("대표자명을 입력하세요.")
        .max(20, "최대 20자까지 가능합니다."),
    phone: yup
        .string()
        .max(11, "전화번호는 최대 11자입니다.")
        .matches(/^[0-9]*$/, "숫자만 입력 가능합니다."),
    addr1: yup
        .string()
        .required("사업장 주소를 검색하세요."),
    addr2: yup
        .string()
        .max(50, "상세주소는 최대 50자까지 가능합니다."),
    sizeCategory: yup
        .string()
        .oneOf(["1", "2"], "사내규모를 선택하세요.")
        .required("사내규모를 선택하세요."),

    bizType: yup
        .string()
        .required("업태를 선택하세요."),
    bizItem: yup
        .string()
        .max(50, "종목은 최대 50자까지 가능합니다."),
    notice: yup
        .string()
        .max(666, "비고는 최대 666자까지 가능합니다."),
    engCompName: yup
        .string()
        .required("ID 회사명을 입력하세요.")
        .max(100, "ID 회사명은 최대 100자까지 가능합니다.")
        .matches(/^[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ]*$/, "특수문자는 입력할 수 없습니다."),
});

export function useCorpreg({ onAccountCreated } = {}) {
    const { auth } = useContext(AuthContext);
    const isUser = (auth.role === ROLES.COMP_ADMIN || auth.role === ROLES.USER);
    const isAdmin = (auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN);
    const isSuperAdmin = auth.role === ROLES.SUPER_ADMIN;
    const isR1Admin = auth.role === ROLES.ADMIN;
    const currentUser = { id: auth.userId, name: auth.name };

    // 1) react-hook-form
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            compCode: '',
            region: '',
            bizNo: '',
            compName: '',
            repName: '',
            phone: '',
            addr1: '',
            addr2: '',
            sizeCategory: '',
            bizType: '',
            bizItem: '',
            notice: '',
            engCompName: '',
            managerIds: isR1Admin ? (auth.userId ? [auth.userId] : []) : [],
            useYn: 'Y',
        },
    });
    const {
        setValue,
        handleSubmit,
        watch,
    } = methods;

    // 2) 관할구역 목록
    const [regionList, setRegionList] = useState([]);

    // 업태 목록
    const [bizTypeList, setBizTypeList] = useState([]);

    // 담당자 목록 (R0만 사용)
    const [managerList, setManagerList] = useState([]);

    // 3) IME 한글 대응
    const [isComposing, setIsComposing] = useState(false);

    // 관할구역 목록 조회
    useEffect(() => {
        const fetchRegionCodes = async () => {
            try {
                const res = await get('/user/corp/regionCodes');
                if (res.status !== false) {
                    setRegionList(res.result || []);
                }
            } catch (err) {
                console.error('관할구역 목록 조회 실패', err);
            }
        };
        fetchRegionCodes();
    }, []);

    // 업태 목록 조회
    useEffect(() => {
        get('/user/corp/bizTypeCodes')
            .then((res) => {
                if (res.status !== false) setBizTypeList(res.result || []);
            })
            .catch((err) => console.error('업태 목록 조회 실패', err));
    }, []);

    // 담당자 목록 조회 (R0, R1)
    useEffect(() => {
        if (!isSuperAdmin && !isR1Admin) return;
        get('/admin/corp/managerList')
            .then((res) => {
                if (res.status !== false && res.result) setManagerList(res.result);
            })
            .catch((err) => console.error('담당자 목록 조회 실패', err));
    }, [isSuperAdmin, isR1Admin]);

    // R1 계정은 담당자 목록 로드 후 자신을 기본값으로 설정
    useEffect(() => {
        if (isR1Admin && auth.userId && managerList.length > 0) {
            setValue('managerIds', [auth.userId]);
        }
    }, [isR1Admin, auth.userId, managerList, setValue]);

    // (유저) compCode 기반 사업장 자동 조회
    useEffect(() => {
        if (isUser && auth.compCode) {
            const fetchCompany = async () => {
                try {
                    const res = await get(`/user/corp/${auth.compCode}`);
                    if (res.status !== false && res.result) {
                        // 응답 결과를 폼에 세팅
                        Object.entries(res.result).forEach(([key, value]) => {
                            setValue(key, value || '');
                        });
                    }
                } catch (err) {
                    console.error('사업장 조회 실패', err);
                }
            };
            fetchCompany();
        }
    }, [isUser, auth, setValue]);

    // 등록/수정
    const onSave = async (formData) => {
        // 저장 전 confirm
        if (!window.confirm("저장하시겠습니까?")) {
            return;
        }
        try {
            const hasCompCode = formData.compCode && formData.compCode.trim() !== '';
            let res = null;
            if (hasCompCode) {
                // Update
                res = await put(`/user/corp/${formData.compCode}`, formData);
            } else {
                // Insert
                res = await post('/user/corp/createCompany', formData);
            }
            if (res.status === false) {
                toast.error(res.message || '오류가 발생했습니다.');
                return;
            }

            if (!hasCompCode) {
                const company = res.result?.company;
                const account = res.result?.account;
                if (company?.compCode) {
                    setValue('compCode', company.compCode);
                }
                if (account && typeof onAccountCreated === 'function') {
                    onAccountCreated(account);
                } else {
                    toast.success(res.message || '등록 성공');
                    window.location.reload();
                }
            } else {
                toast.success(res.message || '수정 성공');
            }
        } catch (err) {
            console.error('저장 오류', err);
            console.error('저장 오류 상세:', err.response?.data?.message);
        }
    };

    // 숫자 이외 키 차단
    const handleNumericKeyDown = (e) => {
        if (isComposing) return;
        const allowKeys = [
            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'
        ];
        if (allowKeys.includes(e.key)) return;

        if (e.shiftKey || e.altKey || e.ctrlKey) {
            e.preventDefault();
            return;
        }
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleNumericPaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain') || '';
        const onlyDigits = text.replace(/\D/g, '');
        setValue(e.target.name, onlyDigits, { shouldValidate: true });
    };

    const handleNumericDrop = (e) => {
        e.preventDefault();
        const text = e.dataTransfer.getData('text/plain') || '';
        const onlyDigits = text.replace(/\D/g, '');
        setValue(e.target.name, onlyDigits, { shouldValidate: true });
    };

    const handleCompositionStart = () => setIsComposing(true);
    const handleCompositionEnd = (e) => {
        setIsComposing(false);
        const onlyDigits = e.target.value.replace(/\D/g, '');
        e.target.value = onlyDigits;
        setValue(e.target.name, onlyDigits, { shouldValidate: true });
    };

    return {
        ...methods,
        isUser,
        isAdmin,
        isSuperAdmin,
        isR1Admin,
        currentUser,
        regionList,
        bizTypeList,
        managerList,
        isComposing,
        setIsComposing,
        handleFormSubmit: handleSubmit(onSave),
        handleNumericKeyDown,
        handleNumericPaste,
        handleNumericDrop,
        handleCompositionStart,
        handleCompositionEnd,
    };
}
